import React, { useState, useEffect, useMemo, useContext } from "react";
import { History, Mail, Plus, RefreshCw, Trash2 } from "lucide-react";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import SearchBar from "../components/SearchBar";
import SortableTable from "../components/SortableTable";
import PaginationControls from "../components/PaginationControls";
import ExportButton from "../components/ExportButton";
import Button from "../../../common/Button";
import CreateApiKeyModal from "./APIKeysList/CreateApiKeyModal";
import ApiKeyHistoryModal from "./APIKeysList/ApiKeyHistoryModal";
import "../style.css";
import "./RolesList.css";
import "./APIKeysList.css";

function getKeyStatus(apiKey) {
  if (apiKey.email_verified) {
    return { label: "Verified", className: "api-key-status-badge--active" };
  }
  if (apiKey.sent_at) {
    return {
      label: "Pending Verification",
      className: "api-key-status-badge--pending",
    };
  }
  return { label: "Not Sent", className: "api-key-status-badge--inactive" };
}

export default function APIKeysList() {
  const {
    apiKeys,
    fetchApiKeys,
    deleteApiKey,
    updateApiKey,
    reverifyApiKey,
    accessGroups,
    fetchGroups,
  } = useContext(FieldConfigurationContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [paginationConfig, setPaginationConfig] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [historyApiKey, setHistoryApiKey] = useState(null);

  useEffect(() => {
    fetchApiKeys();
    fetchGroups();
  }, [fetchApiKeys, fetchGroups]);

  const groupNameByUuid = useMemo(() => {
    const map = {};
    (accessGroups.data || []).forEach((group) => {
      map[group.uuid] = group.name;
    });
    return map;
  }, [accessGroups.data]);

  const resolveGroupNames = (apiKey) =>
    (apiKey.group_list || []).map(
      (uuid) => groupNameByUuid[uuid] || "Unknown Access Group"
    );

  const filteredKeys = useMemo(() => {
    const data = apiKeys.data;
    if (!searchTerm) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter((apiKey) => {
      const groupNames = resolveGroupNames(apiKey).join(", ").toLowerCase();
      return (
        apiKey.name?.toLowerCase().includes(searchLower) ||
        apiKey.email?.toLowerCase().includes(searchLower) ||
        groupNames.includes(searchLower)
      );
    });
  }, [apiKeys.data, searchTerm, groupNameByUuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedKeys = useMemo(() => {
    if (!sortConfig.key) return filteredKeys;

    return [...filteredKeys].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "group_list") {
        aValue = resolveGroupNames(a).join(", ");
        bValue = resolveGroupNames(b).join(", ");
      }

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "boolean") {
        return sortConfig.direction === "asc"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      }

      return 0;
    });
  }, [filteredKeys, sortConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const paginatedKeys = useMemo(() => {
    const startIndex =
      (paginationConfig.currentPage - 1) * paginationConfig.pageSize;
    return sortedKeys.slice(startIndex, startIndex + paginationConfig.pageSize);
  }, [sortedKeys, paginationConfig]);

  useEffect(() => {
    setPaginationConfig((prev) => ({ ...prev, currentPage: 1 }));
  }, [filteredKeys.length]);

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleVerify = async (apiKey, event) => {
    event.stopPropagation();
    if (
      !window.confirm(
        `Send a verification email to ${apiKey.email} for "${apiKey.name}"?`
      )
    ) {
      return;
    }
    const result = await reverifyApiKey(apiKey.uuid);
    if (result.success) {
      alert(`Verification email sent to ${apiKey.email}.`);
    } else {
      alert(
        result.error ||
          `Failed to send verification email for "${apiKey.name}". Please try again.`
      );
    }
  };

  const handleToggleActive = async (apiKey, event) => {
    event.stopPropagation();
    const result = await updateApiKey(apiKey.uuid, {
      enabled: !apiKey.enabled,
    });
    if (!result.success) {
      alert(
        result.error || `Failed to update "${apiKey.name}". Please try again.`
      );
    }
  };

  const handleDelete = async (apiKey, event) => {
    event.stopPropagation();
    if (
      !window.confirm(
        `Are you sure you want to delete "${apiKey.name}"?\n\nThis will permanently disable access for this API key and cannot be undone.`
      )
    ) {
      return;
    }
    const result = await deleteApiKey(apiKey.uuid);
    if (!result.success) {
      alert(
        result.error ||
          `Failed to delete API key "${apiKey.name}". Please try again or contact support if the issue persists.`
      );
    }
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      width: "200px",
      render: (apiKey, value) => (
        <span
          style={{
            fontWeight: 500,
            color: "rgba(46, 50, 66, 1)",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "200px",
          }}
          title={value || ""}
        >
          {value}
        </span>
      ),
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      width: "220px",
      render: (apiKey, value) => (
        <span
          style={{
            color: "rgba(100, 116, 139, 1)",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "220px",
          }}
          title={value || ""}
        >
          {value || "—"}
        </span>
      ),
    },
    {
      key: "group_list",
      label: "Access Groups",
      sortable: true,
      width: "300px",
      render: (apiKey) => {
        const names = resolveGroupNames(apiKey);
        if (names.length === 0)
          return <span style={{ color: "rgba(100, 116, 139, 1)" }}>—</span>;
        return (
          <div className="access-code-tags">
            {names.map((name, index) => (
              <span key={index} className="access-code-tag" title={name}>
                {name}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "email_verified",
      label: "Status",
      sortable: true,
      width: "160px",
      render: (apiKey) => {
        const status = getKeyStatus(apiKey);
        return (
          <span className={`api-key-status-badge ${status.className}`}>
            {status.label}
          </span>
        );
      },
    },
    {
      key: "enabled",
      label: "Active",
      sortable: true,
      width: "100px",
      render: (apiKey) => (
        <button
          type="button"
          className={`api-key-active-toggle ${
            apiKey.enabled
              ? "api-key-active-toggle--on"
              : "api-key-active-toggle--off"
          }`}
          onClick={(e) => handleToggleActive(apiKey, e)}
          title={apiKey.enabled ? "Click to deactivate" : "Click to activate"}
        >
          {apiKey.enabled ? "Active" : "Inactive"}
        </button>
      ),
    },
  ];

  const exportColumns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email", format: (val) => val || "" },
    {
      key: "group_list",
      label: "Access Groups",
      format: (val, row) => resolveGroupNames(row).join(", "),
    },
    {
      key: "email_verified",
      label: "Status",
      format: (val, row) => getKeyStatus(row).label,
    },
    {
      key: "enabled",
      label: "Active",
      format: (val) => (val ? "Active" : "Inactive"),
    },
  ];

  const handleShowHistory = (apiKey, event) => {
    event.stopPropagation();
    setHistoryApiKey(apiKey);
  };

  const actions = [
    {
      label: "Verify",
      icon: Mail,
      onClick: handleVerify,
    },
    {
      label: "History",
      icon: History,
      onClick: handleShowHistory,
    },
    {
      label: "Delete",
      icon: Trash2,
      onClick: handleDelete,
      className: "action-button-delete",
    },
  ];

  return (
    <div className="roles-list">
      <div className="roles-list__header">
        <div className="roles-list__header-top">
          <div className="roles-list__header-left">
            <h2 className="roles-list__title">API Management</h2>
          </div>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search API keys..."
          />
          <div className="roles-list__actions">
            <ExportButton
              data={sortedKeys}
              filename="api_keys_export"
              columns={exportColumns}
              className="button--secondary"
            />
            <Button
              className="button--secondary"
              onClick={() => fetchApiKeys(true)}
              disabled={apiKeys.loading}
              title="Refresh API keys"
            >
              <RefreshCw size={18} style={{ marginRight: "6px" }} />
              Refresh
            </Button>
            <Button
              className="button--primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} style={{ marginRight: "6px" }} />
              Create API Key
            </Button>
          </div>
        </div>
      </div>

      {apiKeys.loading && (
        <div className="roles-list__loading">
          <p>Loading API keys...</p>
        </div>
      )}

      {apiKeys.error && (
        <div className="roles-list__error">
          <p>Error loading API keys: {apiKeys.error}</p>
          <Button onClick={() => fetchApiKeys(true)}>Retry</Button>
        </div>
      )}

      {!apiKeys.loading && !apiKeys.error && (
        <>
          <SortableTable
            data={paginatedKeys}
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
            actions={actions}
            emptyMessage="No API keys found. Click 'Create API Key' to create your first key."
          />
          <PaginationControls
            currentPage={paginationConfig.currentPage}
            pageSize={paginationConfig.pageSize}
            totalItems={sortedKeys.length}
            onPageChange={(newPage) => {
              setPaginationConfig((prev) => ({
                ...prev,
                currentPage: newPage,
              }));
            }}
          />
        </>
      )}

      <CreateApiKeyModal
        open={showCreateModal}
        accessGroups={accessGroups}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => setShowCreateModal(false)}
      />

      <ApiKeyHistoryModal
        apiKey={historyApiKey}
        onClose={() => setHistoryApiKey(null)}
      />
    </div>
  );
}
